import type { CampaignAnalysisCreatorProfile } from '@/entities/campaign/generated/campaign-type';
import { InstagramColorLogo, TikTokLogo } from '@/shared/icons/index';

export const MAX_INSIGHT_RATIO = 2.6;
export const BASELINE_INSIGHT_RATIO = 1;
export const MAX_REPORT_INSIGHTS_COUNT = 5;

export const compositionChartColors = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--muted-foreground)',
] as const;

export const platformLogoItems = [
  { key: 'tiktok', label: 'TikTok', Logo: TikTokLogo },
  { key: 'instagram', label: 'Instagram', Logo: InstagramColorLogo },
] as const;

export type ReportPlatformLogoItem = (typeof platformLogoItems)[number];

export const reportSectionMap = {
  executiveSummary: { id: 'executive-summary', label: 'Executive summary' },
  overview: { id: 'overview', label: 'Overview' },
  topVideos: { id: 'top-videos', label: 'Top 5 videos' },
  insights: { id: 'insights', label: 'Insights' },
  contentGuide: { id: 'content-guide', label: 'Action guide' },
  creatorGuide: { id: 'creator-guide', label: 'Creator guide' },
  appendix: { id: 'appendix', label: 'Appendix' },
} as const;

export const reportSectionNavItems = [
  reportSectionMap.executiveSummary,
  reportSectionMap.overview,
  reportSectionMap.topVideos,
  reportSectionMap.insights,
  reportSectionMap.contentGuide,
  reportSectionMap.creatorGuide,
  reportSectionMap.appendix,
] as const;

export type ReportSectionId = (typeof reportSectionNavItems)[number]['id'];

export function getTopVideoPlatform(sourceUrl: string): ReportPlatformLogoItem {
  return sourceUrl.toLowerCase().includes('instagram.com')
    ? platformLogoItems[1]
    : platformLogoItems[0];
}

export function getCreatorProfileUrl(platform: ReportPlatformLogoItem, handle: string) {
  const normalizedHandle = encodeURIComponent(handle.replace(/^@+/, ''));

  if (platform.key === 'instagram') {
    return `https://www.instagram.com/${normalizedHandle}`;
  }

  return `https://www.tiktok.com/@${normalizedHandle}`;
}

export function getCreatorProfileParts(profile: CampaignAnalysisCreatorProfile | undefined) {
  if (!profile) return [];

  return Object.values(profile).filter((value): value is string => Boolean(value));
}

export function getSummaryCardHeading(heading: string) {
  const [, headingWithoutLegacyPrefix = heading] = heading.split('·');
  return headingWithoutLegacyPrefix.trim();
}

const compactNumberFormatter = new Intl.NumberFormat('en', {
  maximumFractionDigits: 2,
  notation: 'compact',
});
const wholeNumberFormatter = new Intl.NumberFormat('en');
const percentFormatter = new Intl.NumberFormat('en', {
  maximumFractionDigits: 2,
  style: 'percent',
});

export function formatCompactNumber(value: number) {
  return compactNumberFormatter.format(value);
}

export function formatWholeNumber(value: number) {
  return wholeNumberFormatter.format(value);
}

export function formatPercent(value: number) {
  return percentFormatter.format(value);
}
